import React, { useRef, useState } from "react";
import { Upload, Plus, X } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

export const MultiImageField = ({ label = "Photos", value = [], onChange, testId = "multi-image" }) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState("");
  const images = value.filter(Boolean);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const added = [];
      for (const f of files.slice(0, 6)) {
        if (f.size > 4 * 1024 * 1024) { toast.error(`${f.name} is over 4 MB — skipped`); continue; }
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        added.push(r.data.url);
      }
      if (added.length) { onChange([...images, ...added]); toast.success(`${added.length} photo${added.length > 1 ? "s" : ""} added`); }
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const addUrl = () => {
    const u = url.trim();
    if (!u.startsWith("http")) { toast.error("Enter a full https:// image URL"); return; }
    onChange([...images, u]);
    setUrl("");
  };

  const remove = (i) => onChange(images.filter((_, idx) => idx !== i));
  const makeMain = (i) => { const arr = [...images]; const [x] = arr.splice(i, 1); onChange([x, ...arr]); };

  return (
    <div data-testid={testId}>
      <span className="eyebrow">{label} {images.length > 0 && `(${images.length} — first is the main photo)`}</span>
      <div className="mt-3 flex flex-wrap gap-2">
        {images.map((img, i) => (
          <div key={`${img}-${i}`} className={`relative group w-16 h-16 border ${i === 0 ? "border-[var(--twr-bronze)]" : "border-[var(--twr-line-strong)]"}`} data-testid={`${testId}-thumb-${i}`}>
            <img src={img} alt="" className="w-full h-full object-cover" />
            {i === 0 && <span className="absolute bottom-0 left-0 right-0 bg-[var(--twr-bronze)] text-[#0A0A0A] text-[8px] tracking-[0.12em] uppercase text-center">Main</span>}
            <button type="button" onClick={() => remove(i)} data-testid={`${testId}-remove-${i}`} title="Remove"
              className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-black border border-[var(--twr-line-strong)] text-[var(--twr-warm-white)] hover:text-red-400 hover:border-red-400 items-center justify-center hidden group-hover:flex">
              <X size={9} />
            </button>
            {i !== 0 && (
              <button type="button" onClick={() => makeMain(i)} title="Make main photo"
                className="absolute inset-0 bg-black/60 text-[9px] tracking-[0.1em] uppercase text-[var(--twr-bronze)] opacity-0 group-hover:opacity-100 transition-opacity">
                Set main
              </button>
            )}
          </div>
        ))}
        <input ref={fileRef} type="file" accept="image/*" multiple onChange={upload} className="hidden" data-testid={`${testId}-file`} />
        <button type="button" disabled={busy} onClick={() => fileRef.current?.click()} data-testid={`${testId}-upload-btn`}
          className="w-16 h-16 border border-dashed border-[var(--twr-line-strong)] hover:border-[var(--twr-bronze)] hover:text-[var(--twr-bronze)] transition-colors flex flex-col items-center justify-center gap-1 text-[9px] tracking-[0.1em] uppercase text-[var(--twr-warm-white)]/55">
          <Upload size={13} /> {busy ? "…" : "Upload"}
        </button>
      </div>
      <div className="mt-2 flex gap-2">
        <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="or paste an image URL"
          data-testid={`${testId}-url-input`}
          className="flex-1 min-w-0 bg-transparent border border-[var(--twr-line-strong)] focus:border-[var(--twr-bronze)] outline-none px-3 py-2 text-[12px]" />
        <button type="button" disabled={!url.trim()} onClick={addUrl} data-testid={`${testId}-url-add`}
          className="shrink-0 inline-flex items-center gap-1 border border-[var(--twr-line-strong)] hover:border-[var(--twr-bronze)] hover:text-[var(--twr-bronze)] transition-colors px-3 py-2 text-[11px] tracking-[0.14em] uppercase">
          <Plus size={11} /> Add
        </button>
      </div>
    </div>
  );
};
